import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Alert,
  Chip,
  Tooltip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  QrCode as QrCodeIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { QRCodeSVG } from 'qrcode.react';

interface SurveyLink {
  id: string;
  surveyId: string;
  surveyName: string;
  token: string;
  url: string;
  requiresAgentCode: boolean;
  expiresAt?: string;
  createdAt: string;
  submissionCount: number;
  isActive: boolean;
}

const SurveyLinks: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin, isAgence, isSOBEBRA } = usePermissions();
  const [links, setLinks] = useState<SurveyLink[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openQRDialog, setOpenQRDialog] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    surveyId: '',
    surveyName: '',
    requiresAgentCode: false,
    expiresAt: '',
  });

  const canManageLinks = isAdmin || isAgence || isSOBEBRA;

  useEffect(() => {
    loadLinks();
  }, []);

  const loadLinks = () => {
    // Simuler le chargement des liens
    const mockLinks: SurveyLink[] = [
      {
        id: 'link-1',
        surveyId: 'survey-1',
        surveyName: 'Enquête Consommateurs - Marchés Cotonou',
        token: 'public',
        url: `${window.location.origin}/survey/public/survey-1/public`,
        requiresAgentCode: false,
        expiresAt: '2024-12-31T23:59:59Z',
        createdAt: '2024-11-01T10:00:00Z',
        submissionCount: 45,
        isActive: true,
      },
      {
        id: 'link-2',
        surveyId: 'survey-2',
        surveyName: 'Sondage Agents Terrain - Activation PDV',
        token: 'secure',
        url: `${window.location.origin}/survey/secure/survey-2/secure`,
        requiresAgentCode: true,
        expiresAt: '2024-11-30T23:59:59Z',
        createdAt: '2024-11-10T09:00:00Z',
        submissionCount: 12,
        isActive: true,
      },
    ];
    setLinks(mockLinks);
  };

  const generateToken = (): string => {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  };

  const generateShortUrl = (surveyId: string, token: string): string => {
    const shortCode = token.substring(0, 8).toUpperCase();
    return `${window.location.origin}/survey/${token}/${surveyId}/${token}`;
  };

  const handleCreateLink = () => {
    if (!formData.surveyId || !formData.surveyName) {
      return;
    }

    const token = generateToken();
    const newLink: SurveyLink = {
      id: 'link-' + Date.now(),
      surveyId: formData.surveyId,
      surveyName: formData.surveyName,
      token,
      url: generateShortUrl(formData.surveyId, token),
      requiresAgentCode: formData.requiresAgentCode,
      expiresAt: formData.expiresAt || undefined,
      createdAt: new Date().toISOString(),
      submissionCount: 0,
      isActive: true,
    };

    setLinks([newLink, ...links]);
    setOpenDialog(false);
    setFormData({
      surveyId: '',
      surveyName: '',
      requiresAgentCode: false,
      expiresAt: '',
    });
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    // En production, afficher un toast de confirmation
    alert('Lien copié dans le presse-papiers !');
  };

  const handleDeleteLink = (linkId: string) => {
    setLinks(links.filter(l => l.id !== linkId));
  };

  const isExpired = (expiresAt?: string): boolean => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  if (!canManageLinks) {
    return (
      <Box>
        <Alert severity="info">
          Vous n'avez pas les permissions nécessaires pour gérer les liens de sondage.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Liens de Collecte de Données</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Générer un nouveau lien
        </Button>
      </Box>

      {/* Statistiques */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Liens Actifs
              </Typography>
              <Typography variant="h4">
                {links.filter(l => l.isActive && !isExpired(l.expiresAt)).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Soumissions
              </Typography>
              <Typography variant="h4">
                {links.reduce((sum, l) => sum + l.submissionCount, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Liens Expirés
              </Typography>
              <Typography variant="h4" color="error">
                {links.filter(l => isExpired(l.expiresAt)).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Liste des liens */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sondage</TableCell>
              <TableCell>Lien</TableCell>
              <TableCell>Sécurité</TableCell>
              <TableCell>Expiration</TableCell>
              <TableCell>Soumissions</TableCell>
              <TableCell>Statut</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {links.length === 0 ? (
              <TableRow>
                {/* @ts-ignore - colSpan is valid HTML attribute */}
                <TableCell colSpan={7} align="center">
                  Aucun lien généré
                </TableCell>
              </TableRow>
            ) : (
              links.map((link) => (
                <TableRow key={link.id}>
                  <TableCell>
                    <Typography variant="body1" fontWeight="medium">
                      {link.surveyName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {link.surveyId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {link.url}
                      </Typography>
                      <Tooltip title="Copier le lien">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyLink(link.url)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {link.requiresAgentCode ? (
                      <Chip label="Code Agent" size="small" color="warning" />
                    ) : (
                      <Chip label="Public" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell>
                    {link.expiresAt ? (
                      <Typography variant="body2">
                        {new Date(link.expiresAt).toLocaleDateString('fr-FR')}
                        {isExpired(link.expiresAt) && (
                          <Chip label="Expiré" size="small" color="error" sx={{ ml: 1 }} />
                        )}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Sans expiration
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">
                      {link.submissionCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {link.isActive ? (
                      <Chip label="Actif" size="small" color="success" />
                    ) : (
                      <Chip label="Inactif" size="small" color="default" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Voir QR Code">
                      <IconButton
                        size="small"
                        onClick={() => setOpenQRDialog(link.id)}
                      >
                        <QrCodeIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ouvrir le lien">
                      <IconButton
                        size="small"
                        onClick={() => window.open(link.url, '_blank')}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteLink(link.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog Création de Lien */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Générer un nouveau lien</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="ID du Sondage"
            margin="normal"
            required
            value={formData.surveyId}
            onChange={(e) => setFormData({ ...formData, surveyId: e.target.value })}
            helperText="Identifiant unique du sondage"
          />
          <TextField
            fullWidth
            label="Nom du Sondage"
            margin="normal"
            required
            value={formData.surveyName}
            onChange={(e) => setFormData({ ...formData, surveyName: e.target.value })}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.requiresAgentCode}
                onChange={(e) => setFormData({ ...formData, requiresAgentCode: e.target.checked })}
              />
            }
            label="Nécessite un code agent (accès sécurisé)"
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Date d'expiration (optionnel)"
            margin="normal"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={formData.expiresAt}
            onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
            helperText="Le lien sera désactivé après cette date"
          />
          <Alert severity="info" sx={{ mt: 2 }}>
            Un QR Code sera généré automatiquement pour faciliter le déploiement sur le terrain.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
          <Button onClick={handleCreateLink} variant="contained" disabled={!formData.surveyId || !formData.surveyName}>
            Générer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog QR Code */}
      <Dialog open={openQRDialog !== null} onClose={() => setOpenQRDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>QR Code</DialogTitle>
        <DialogContent>
          {openQRDialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
              {(() => {
                const link = links.find(l => l.id === openQRDialog);
                return link ? (
                  <>
                    <QRCodeSVG value={link.url} size={200} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                      Scannez ce QR Code pour accéder au questionnaire
                    </Typography>
                    <Button
                      startIcon={<ContentCopyIcon />}
                      onClick={() => handleCopyLink(link.url)}
                      sx={{ mt: 2 }}
                    >
                      Copier le lien
                    </Button>
                  </>
                ) : null;
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQRDialog(null)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SurveyLinks;

